import { useEffect, useState } from 'react';
import type { Phase, RoadmapLevel } from '@shared/roadmap';

const PHASE_LABEL: Record<Phase, string> = {
  immediate: '即応(1ヶ月以内)',
  short_term: '短期(3ヶ月以内)',
  mid_term: '中期(6ヶ月以内)',
};

const LEVEL_LABEL: Record<RoadmapLevel, string> = {
  1: '危機',
  2: '要改善',
  4: '良好',
  5: '最良',
};

const LEVEL_PILL: Record<RoadmapLevel, string> = {
  1: 'bg-rank-1 text-white',
  2: 'bg-rank-2 text-white',
  4: 'bg-rank-4 text-white',
  5: 'bg-rank-5 text-white',
};

export interface RoadmapEditTarget {
  factor_id: number;
  factor_name: string;
  layer_name: string;
  category_name: string;
  level: RoadmapLevel;
  phase: Phase;
  /** Currently displayed action (override or default). Used as the textarea seed. */
  current_action: string;
  /** Whether an override exists at this layer (controls Revert button visibility). */
  isCustomized: boolean;
  /** Wording for the revert button — defaults to "テンプレートに戻す". */
  revertLabel?: string;
}

interface Props {
  target: RoadmapEditTarget | null;
  onClose: () => void;
  onSave: (newAction: string) => Promise<void>;
  onRevert: () => Promise<void>;
}

const MAX_LENGTH = 600;

export function RoadmapEditModal({ target, onClose, onSave, onRevert }: Props) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState<null | 'saving' | 'reverting'>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (target) {
      setText(target.current_action);
      setError(null);
      setBusy(null);
    }
  }, [target]);

  if (!target) return null;

  const trimmed = text.trim();
  const unchanged = trimmed === target.current_action.trim();
  const tooLong = trimmed.length > MAX_LENGTH;

  const handleSave = async () => {
    if (!trimmed || tooLong) return;
    setBusy('saving');
    setError(null);
    try {
      await onSave(trimmed);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(null);
    }
  };

  const handleRevert = async () => {
    if (!confirm('カスタマイズを削除してテンプレートに戻します。よろしいですか?')) return;
    setBusy('reverting');
    setError(null);
    try {
      await onRevert();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : '戻すのに失敗しました');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4 no-print"
      onClick={() => !busy && onClose()}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <p className="text-xs text-slate-500">
              {target.layer_name} / {target.category_name} ・ {PHASE_LABEL[target.phase]}
            </p>
            <h2 className="text-lg font-bold text-slate-800">
              <span className="mr-2 text-xs text-slate-400">#{target.factor_id}</span>
              {target.factor_name}
              <span
                className={`ml-2 rounded px-1.5 py-0.5 align-middle text-[10px] font-bold ${LEVEL_PILL[target.level]}`}
              >
                Lv.{target.level} {LEVEL_LABEL[target.level]}
              </span>
            </h2>
          </div>
          <button
            type="button"
            onClick={() => !busy && onClose()}
            className="text-slate-400 hover:text-slate-600"
            aria-label="閉じる"
          >
            ✕
          </button>
        </div>

        <label className="label">アクション文</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={8}
          className="input font-normal text-sm leading-relaxed"
          disabled={busy !== null}
          placeholder="このクライアント向けに具体化したアクションを入力..."
        />
        <div className="mt-1 flex items-center justify-between text-xs">
          <span className={tooLong ? 'text-rank-1' : 'text-slate-500'}>
            {trimmed.length} / {MAX_LENGTH} 文字
          </span>
          {target.isCustomized && (
            <span className="text-brand-accent">編集済み(カスタマイズ反映中)</span>
          )}
        </div>

        {error && (
          <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-rank-1">{error}</p>
        )}

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          {target.isCustomized && (
            <button
              type="button"
              onClick={handleRevert}
              disabled={busy !== null}
              className="mr-auto rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60"
            >
              {busy === 'reverting' ? '戻しています...' : (target.revertLabel ?? 'テンプレートに戻す')}
            </button>
          )}
          <button
            type="button"
            onClick={() => !busy && onClose()}
            disabled={busy !== null}
            className="btn-secondary"
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy !== null || !trimmed || tooLong || unchanged}
            className="btn-primary"
          >
            {busy === 'saving' ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}
